import React, { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import moment from 'moment';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  Column,
  ColumnWrapper,
  IconWrapper,
  NoDataRow,
  PaginationButtonWrapper,
  Priority,
  Row,
  TableHeader,
  TableLayout,
  TableWrapper,
  TruncatedTextWrapper,
} from './TaskTable.styles';
import { Icon } from 'bench-internal-theme';
import { Task, TaskStatus } from './types';
import AssigneeColumn from './AssigneeColumn';
import { handleNavigateToTaskAction } from './taskActionLookup';
import { useTaskManagerContext } from './TaskManagerContext';
import { formatScreamingSnakesToPrettyStrings } from './formatScreamingSnakesToPrettyStrings';
import TaskActionColumn from './TaskActionColumn';
import TaskNotesColumn from './TaskNotesColumn';
import { jobDetailsLookup } from './taskDetailsLookup';
import { getTeamFromJobType } from './teamJobLookup';

type TaskTableProps = {
  tasks: Task[];
  onRowClick?: (task: Task) => void;
  paginationFns: {
    getNextPage: VoidFunction | null;
    getPrevPage: VoidFunction | null;
  };
  assignTask?: (task: Task) => Promise<Record<string, any> | undefined>;
  isClientTaskManager?: boolean;
  isBpaView?: boolean;
};

// Can't add a display name here because of the Angular tests
// eslint-disable-next-line react/display-name
const TaskTable = React.forwardRef<HTMLTableElement, TaskTableProps>(
  (
    {
      tasks = [],
      onRowClick,
      paginationFns,
      assignTask,
      isClientTaskManager = false,
      isBpaView = false,
    },
    ref,
  ) => {
    const { resolveTask, resolveTaskLoading, loggedInPerson } = useTaskManagerContext();
    const { getNextPage, getPrevPage } = paginationFns;
    const [sorting, setSorting] = useState<SortingState>([]);

    const handleActionButtonClick = useCallback(
      async (task: Task, event: SyntheticEvent) => {
        event.stopPropagation();
        let assignedPerson = task.assignedPerson;
        if (!task.assignedPerson && task.status === TaskStatus.PENDING_WORK && assignTask) {
          const data = await assignTask(task);
          assignedPerson = data?.assignTask?.assignedPerson?.id;
        }
        if (assignedPerson && task.job?.type && task.job?.client?.id && task.type) {
          handleNavigateToTaskAction(task.job.type, task.type, task.job.client.id);
        }
      },
      [assignTask],
    );

    const columns = useMemo<ColumnDef<Task>[]>(() => {
      const clientIdCol: ColumnDef<Task> = {
        accessorFn: row => row.job?.client?.id,
        id: 'clientId',
        header: 'Client ID',
        cell: info => <Column>{info.getValue()}</Column>,
      };

      const clientNameCol: ColumnDef<Task> = {
        accessorFn: row => row.job?.client?.businessName,
        id: 'clientName',
        header: 'Client',
        cell: info => (
          <Column>
            <TruncatedTextWrapper>{info.getValue()}</TruncatedTextWrapper>
          </Column>
        ),
      };

      const assigneeCol: ColumnDef<Task> = {
        accessorFn: row => row.assignedPerson?.fullName,
        id: 'assignee',
        header: 'Assignee',
        cell: info => {
          return (
            <AssigneeColumn
              task={info.row.original}
              assignTask={assignTask}
              isClientTaskManager={isClientTaskManager}
            />
          );
        },
      };

      const taskTypeCol: ColumnDef<Task> = {
        accessorKey: 'type',
        header: 'Job / Task',
        cell: info => {
          const type: string = info.getValue();
          const jobType =
            info.row.original.job?.type &&
            formatScreamingSnakesToPrettyStrings(info.row.original.job?.type);
          const taskType = formatScreamingSnakesToPrettyStrings(type);
          return <Column>{(jobType ? jobType + ', ' : '') + taskType}</Column>;
        },
      };

      const inboxTypeCol: ColumnDef<Task> = {
        accessorFn: row => row.job?.client?.inboxState,
        id: 'inboxType',
        header: 'Inbox Type',
        cell: info => <Column>{formatScreamingSnakesToPrettyStrings(info.getValue())}</Column>,
      };

      const priorityCol: ColumnDef<Task> = {
        accessorFn: row => row.job?.priority,
        header: 'Priority',
        cell: info => {
          const priority: number = info.getValue();
          const escalated = info.row.original.job?.escalatedOn;
          return (
            <Priority>
              {escalated ? <Icon icon="arrowCircleUp" color="#3D898B" /> : ''}
              {priority}
            </Priority>
          );
        },
      };

      const statusCol: ColumnDef<Task> = {
        accessorKey: 'status',
        header: 'Status',
        cell: info => {
          const taskResolution = info.row.original.resolution;
          const taskStatus: string = info.getValue();
          return (
            <Column>
              {taskStatus === TaskStatus.PENDING_WORK || !taskResolution ? (
                <TaskActionColumn
                  task={info.row.original}
                  handleActionButtonClick={handleActionButtonClick}
                  loggedInPerson={loggedInPerson}
                  resolveTask={resolveTask}
                  resolveTaskLoading={resolveTaskLoading}
                  isClientTaskManager={isClientTaskManager}
                />
              ) : (
                formatScreamingSnakesToPrettyStrings(taskResolution)
              )}
            </Column>
          );
        },
      };

      const createdOnCol: ColumnDef<Task> = {
        accessorFn: row => new Date(row.createdOn),
        id: 'createdOn',
        header: 'Created',
        cell: info => {
          const createdOn = moment(new Date(info.getValue())).format('MMMM D, YYYY'); // omit time
          const daysOld = moment().diff(new Date(createdOn), 'days'); // diff is relative to time 00:00:00
          return (
            <Column>
              {createdOn} - {daysOld} {daysOld === 1 ? 'day' : 'days'} old
            </Column>
          );
        },
        sortingFn: 'datetime',
      };

      const dueDateCol: ColumnDef<Task> = {
        accessorFn: row => (row.job?.dueDate ? new Date(row.job.dueDate) : null),
        id: 'dueDate',
        header: 'Due Date',
        cell: info => {
          const dueDate = info.getValue()
            ? moment.utc(new Date(info.getValue())).format('MMMM D, YYYY')
            : null;
          return <Column>{dueDate}</Column>;
        },
        sortingFn: 'datetime',
      };

      const detailsCol: ColumnDef<Task> = {
        accessorFn: row => {
          const detailsBuilder = jobDetailsLookup[row.job.type];
          return detailsBuilder ? detailsBuilder(row) : '';
        },
        id: 'details',
        header: 'Details',
        cell: info => (
          <Column title={info.getValue()}>
            <TruncatedTextWrapper maxWidth={'500px'}>{info.getValue()}</TruncatedTextWrapper>
          </Column>
        ),
      };

      const notesCol: ColumnDef<Task> = {
        accessorFn: row => row.job?.taskNotes?.length,
        id: 'notes',
        header: 'Notes',
        cell: info => {
          return <TaskNotesColumn taskNotes={info.row.original.job?.taskNotes} />;
        },
      };

      const teamColumn: ColumnDef<Task> = {
        accessorFn: row =>
          formatScreamingSnakesToPrettyStrings(getTeamFromJobType(row.job?.type) ?? ''),
        id: 'team',
        header: 'Team',
        cell: info => <Column>{info.getValue()}</Column>,
      };

      if (isClientTaskManager) {
        return [
          teamColumn,
          taskTypeCol,
          priorityCol,
          detailsCol,
          createdOnCol,
          dueDateCol,
          assigneeCol,
          statusCol,
          notesCol,
        ].filter(item => item !== null);
      } else if (isBpaView) {
        return [
          clientIdCol,
          clientNameCol,
          taskTypeCol,
          statusCol,
          notesCol,
          detailsCol,
          inboxTypeCol,
          priorityCol,
          createdOnCol,
          dueDateCol,
        ].filter(item => item !== null);
      } else {
        return [
          clientIdCol,
          taskTypeCol,
          inboxTypeCol,
          priorityCol,
          detailsCol,
          createdOnCol,
          dueDateCol,
          assigneeCol,
          statusCol,
          notesCol,
        ].filter(item => item !== null);
      }
    }, [
      assignTask,
      handleActionButtonClick,
      isClientTaskManager,
      resolveTask,
      resolveTaskLoading,
      loggedInPerson,
      isBpaView,
    ]);

    const table = useReactTable({
      data: tasks,
      columns,
      state: {
        sorting,
      },
      onSortingChange: setSorting,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
    });

    const headers = table.getHeaderGroups() || [];
    const rows = table.getRowModel()?.rows || [];

    return (
      <TableWrapper>
        <TableLayout ref={ref}>
          <thead>
            {headers.map(headerGroup => (
              <Row key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const columnSort = header.column.getIsSorted();
                  return (
                    <TableHeader key={header.id}>
                      {header.isPlaceholder ? null : (
                        <Column onClick={header.column.getToggleSortingHandler()}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          <IconWrapper>
                            {columnSort
                              ? {
                                  asc: <Icon icon="caretUp" />,
                                  desc: <Icon icon="caretDown" />,
                                }[columnSort]
                              : undefined}
                          </IconWrapper>
                        </Column>
                      )}
                    </TableHeader>
                  );
                })}
              </Row>
            ))}
          </thead>
          <tbody>
            {rows.length ? (
              rows.map((row, index) => {
                return (
                  <Row
                    key={row.id}
                    data-testid={`task-table-row-${row.original.id}`}
                    data-bypass-dismiss-sidebar
                    darkBg={index % 2 == 0}
                    onClick={
                      row.original && onRowClick ? () => onRowClick(row.original) : undefined
                    }
                  >
                    {row.getVisibleCells().map(cell => {
                      return (
                        <ColumnWrapper
                          key={cell.id}
                          width={cell.column.id === 'details' ? '99%' : undefined}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </ColumnWrapper>
                      );
                    })}
                  </Row>
                );
              })
            ) : (
              <NoDataRow>
                <td colSpan={columns.length}>✨ No pending tasks ✨</td>
              </NoDataRow>
            )}
          </tbody>
        </TableLayout>
        <PaginationButtonWrapper>
          <button
            aria-label="get prev page"
            onClick={getPrevPage ? getPrevPage : undefined}
            disabled={!getPrevPage}
          >
            {'<'}
          </button>
          <button
            aria-label="get next page"
            onClick={getNextPage ? getNextPage : undefined}
            disabled={!getNextPage}
          >
            {'>'}
          </button>
        </PaginationButtonWrapper>
      </TableWrapper>
    );
  },
);

export default TaskTable;
